import { CreditIcon } from '@/components/common/CreditIcon'
import { Skeleton, Tooltip, TooltipContent, TooltipTrigger } from '@teach/ui'
import { formatCredits, formatCreditsWithWord, isPositiveCredits } from '@/features/ai/lib/money'
import { useGetCreditsBalanceQuery } from '@/features/ai/creditsApi'
import { useTopUp } from '@/features/ai/credits/CreditsDialogContext'
import { useMessages } from '@/i18n'
import { messages } from './messages'

/**
 * Баланс в шапке редактора. На маршруте редактора сайдбар скрыт целиком
 * (`AppLayout`), поэтому это ЕДИНСТВЕННОЕ место, где автор видит деньги, пока
 * работает: строка цены у кнопки запуска появляется только при открытой панели
 * с приехавшей сметой.
 *
 * Число без слова: «42 кредита» в кластере с тремя кнопками не помещается, а
 * подпись у иконки дублирует значок. Слово живёт в подсказке — вместе с
 * заморозкой, которую иначе негде объяснить.
 *
 * Здесь баланс НЕ округляется, в отличие от плашки профиля: округление там
 * лечило перенос на две строки, а чип — одна строка, переноса не бывает.
 * Терять на нём точность незачем, тем более что тултип рядом печатает ту же
 * сумму словом.
 */
export const CreditsChip = () => {
  const { data, isError } = useGetCreditsBalanceQuery()
  const { open } = useTopUp()
  const m = useMessages(messages)
  // Чтение защитное, как у `useTranslationQuote`: ответ без суммы — это не
  // данные, а `formatCredits(undefined)` бросает и уносит с собой ВСЮ шапку
  // редактора вместе с публикацией и сохранением. Цена ошибки тут выше, чем у
  // плашки в сайдбаре, потому что чип стоит на единственном экране, с которого
  // автору некуда уйти.
  const balance = data?.balance
  const held = isPositiveCredits(data?.held) ? (data?.held ?? null) : null
  const unavailable = isError || (data !== undefined && !balance)

  // Прочерк, а не «0»: ноль у автора с деньгами на счету хуже пустого места
  // (то же правило, что в плашке профиля). Скелет при отказе был бы фальшивой
  // бесконечной загрузкой — ручка в списке молчащих, тост её не покажет.
  const body = unavailable ? (
    <span className="text-sm font-medium">—</span>
  ) : balance ? (
    <span className="text-sm font-medium">{formatCredits(balance)}</span>
  ) : (
    <Skeleton className="h-4 w-8" />
  )

  const hint = balance
    ? held
      ? m.creditsHintHeld(formatCreditsWithWord(balance), formatCredits(held))
      : m.creditsHint(formatCreditsWithWord(balance))
    : m.credits

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={hint}
          onClick={() => open()}
          className="flex shrink-0 items-center gap-1.5 rounded-lg bg-[hsl(var(--credit)/0.15)] px-2 py-1 text-[hsl(var(--credit))] transition-colors hover:bg-[hsl(var(--credit)/0.25)]"
        >
          <CreditIcon />
          {body}
        </button>
      </TooltipTrigger>
      {/* Подсказка — просто название; баланс и заморозку читает скринридер (`aria-label`). */}
      <TooltipContent>{m.credits}</TooltipContent>
    </Tooltip>
  )
}
